import React, { useState, Suspense, useRef } from 'react';
import 'antd/dist/antd.css';
import { useTranslation } from 'react-i18next';
import trTR from 'antd/es/locale/tr_TR';
import { ConfigProvider } from 'antd';
import { Row, Table, Col, Form, Button, Tooltip } from 'antd';
import { ORDER_TYPES } from '../../../../helpers/constants';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';
import { getCancelledItemHistory } from '../../../../store/requests/definitions';
import { CSVLink } from 'react-csv';
import { DownloadOutlined } from '@ant-design/icons';

const CancelledItemHistoryFilter = React.lazy(
  () => import('./CancelledItemHistoryFilter')
);

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 20
  }
};

const CancelledItemHistory = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [filterForm] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCancellations, setFilterCancellations] = useState({});
  const [tableParams, setTableParams] = useState(initialPageParams);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [downloadedData, setDownloadedData] = useState([]);
  const [filters, setFilters] = useState(null);
  const csvLink = useRef();

  const fetchData = (tableParamsValue, filter) => {
    setFilters(filter);
    const queryString = getQueryString(tableParamsValue, filter);
    getCancelledItemHistoryList(queryString);
  };

  const getQueryString = (tableParamsValue, filter) => {
    let queryString = '';

    setFilterCancellations(filter);

    if (Object.values(filter).some((item) => !!item)) {
      Object.entries(filter)
        .filter(([item, value]) => value !== null && value !== undefined)
        .forEach(([item, value], idx) => {
          if (value) {
            queryString += `${item}=${encodeURI(value)}&`;
          }
        });
    }

    queryString = `${queryString}page=${tableParamsValue.pagination.current - 1}&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue.column) {
      queryString += `&sort=${tableParamsValue.column.dataIndex},${ORDER_TYPES[tableParamsValue.order]}`;
    }

    return queryString;
  };

  const getCancelledItemHistoryList = (queryString) => {
    setLoading(true);
    getCancelledItemHistory(queryString)
      .then((response) => {

        const { content, page } = response;

        setRows(content);
        setTableParams({
          pagination: {
            total: page.totalElements,
            current: page.number + 1,
            pageSize: page.size
          }
        });
      })
      .finally(() => setLoading(false));
  };

  const fetchCSVData = async () => {
    setExportButtonLoading(true);
    try {
      if (filters != null) {
        const queryString = getQueryString(initialPageParams, filters);
        const cancellationRes = await getCancelledItemHistory(queryString);
        const cancellationData = cancellationRes.content;

        if (cancellationData && cancellationData.length > 0) {

          const csvData = [
            columns.map((item) => t(item.title)),
            ...cancellationData?.map((item) => [
              item.storeCode,
              item.storeName,
              item.customerOrderNumber,
              item.cancelledDate,
              item.productCode,
              item.erpCode,
              item.productName,
              item.cancellationReason,
              item.quantity,
              item.currentQuantity,
              item.newOrderQuantity,
            ]),
          ];
          setDownloadedData(csvData);
          csvLink.current.link.click();

        }
      }

      setExportButtonLoading(false);

    } catch (error) {
      setExportButtonLoading(false);
    }
  };

  const columns = [
    {
      title: t('storeCode'),
      dataIndex: 'storeCode',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('storeName'),
      dataIndex: 'storeName',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('orderNumber'),
      dataIndex: 'customerOrderNumber',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('cancelledDate'),
      dataIndex: 'cancelledDate',
      sorter: true,
      showSorterTooltip: false,
      render: (text, record) =>
        new Date(text)
          ?.toLocaleDateString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit',
          })
          .toString(),
    },
    {
      title: t('erpCode'),
      dataIndex: 'erpCode',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('productCode'),
      dataIndex: 'productCode',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('productName'),
      dataIndex: 'productName',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('cancellationReason'),
      dataIndex: 'cancellationReason',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('newOrderQuantity'),
      dataIndex: 'newOrderQuantity',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('currentFreeQuantityAtCancellation'),
      dataIndex: 'quantity',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('actualFreeQuantity'),
      dataIndex: 'currentQuantity',
      sorter: true,
      showSorterTooltip: false
    }    
  ];

  const actions = [
    {
      isCrudEnabled: true,
      id: 'downloadButton',
      type: '',
      title: t('download'),
      customContent: (
        <>
          <Button
            shape="round"
            onClick={fetchCSVData}
            icon={<DownloadOutlined />}
            loading={exportButtonLoading}
          />
          <CSVLink
            filename={t('Stores')}
            data={downloadedData}
            ref={csvLink}
            className="hidden"
          />
        </>
      ),
      disabled: false,
      hasDropdown: false,
    }
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: 10,
        }}
      >
        <Col>{renderActionButtons()}</Col>
      </Row>
      <Suspense fallback={<></>}>
        <CancelledItemHistoryFilter
          handleFilter={(filter) => fetchData(initialPageParams, filter)}
          form={filterForm}
          loading={loading}
        />
      </Suspense>
      <Row style={{ marginTop: 10 }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns.map((column) => ({
              ...column,
              sorter: false
            }))}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={(pagination, filters, sorter) => {
              fetchData({ pagination, ...sorter }, filterCancellations);
            }}
            rowKey={(record, idx) =>
              `${record.customerOrderNumber}-${record.productCode}-${idx}`
            }
            scroll={{
              y: '70vh'
            }}
          />
        </ConfigProvider>
      </Row>
    </div>
  );
};

export default CancelledItemHistory;
